import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  MonetizationOn as MoneyIcon,
  CheckCircle as ApproveIcon,
  Person as PersonIcon,
  DateRange as DateIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import { LoadingSpinner } from "../../components/common/Loading";
import { travelAPI } from "../../services/api/travel.api";

const TravelAdvancesNeedingPaymentPage = () => {
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const canView = hasPermission(PERMISSIONS.VIEW_TRAVEL_ADVANCES_NEEDING_PAYMENT);

  const fetchNeedingPayment = async () => {
    try {
      setLoading(true);
      const response = await travelAPI.getTravelAdvancesNeedingPayment({
        page: 1,
        limit: 100,
        _t: Date.now(),
      });
      const data = response?.data?.data ?? response?.data;
      const list = data?.travelAdvances ?? data?.advances ?? (Array.isArray(data) ? data : []);
      setAdvances(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching advances needing payment:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to load advances needing payment";
      showError(msg);
      setAdvances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) fetchNeedingPayment();
  }, [canView]);

  const handleApproveDisbursement = async (advance) => {
    if (!advance?.id) return;
    try {
      setActionLoadingId(advance.id);
      await travelAPI.approveTravelAdvanceDisbursement(advance.id);
      showSuccess("Disbursement approved successfully");
      fetchNeedingPayment();
    } catch (error) {
      console.error("Error approving disbursement:", error);
      showError(error?.response?.data?.message || "Failed to approve disbursement");
    } finally {
      setActionLoadingId(null);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You do not have permission to view this page. Finance Manager or Admin access is required.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
          <WalletIcon color="primary" />
          Travel Advances Needing Payment
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Approve disbursement for fully approved travel advances. Click &quot;Approve disbursement&quot; when payment has been made.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          {advances.length === 0 ? (
            <Alert severity="info">
              No travel advances needing payment at the moment. This list shows advances that have been <strong>fully approved</strong> (by Department Head and then by Finance) and are waiting to be marked as paid.
              <Box component="span" sx={{ display: "block", mt: 1 }}>
                To get advances here: go to <strong>Travel Advance Approvals</strong>, approve as Department Head, then have Finance approve. After that they will appear here so you can mark as paid.
              </Box>
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell><strong>Advance #</strong></TableCell>
                    <TableCell><strong>Employee</strong></TableCell>
                    <TableCell><strong>Destination / Purpose</strong></TableCell>
                    <TableCell><strong>Departure – Return</strong></TableCell>
                    <TableCell align="right"><strong>Amount (TZS)</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {advances.map((adv) => (
                    <TableRow key={adv.id} hover>
                      <TableCell>
                        {adv.advance_number || `#${adv.id}`}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          {adv.user
                            ? [adv.user.first_name, adv.user.sur_name].filter(Boolean).join(" ")
                            : "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {adv.travelRequest
                          ? [adv.travelRequest.destination, adv.travelRequest.purpose].filter(Boolean).join(" – ") || "—"
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <DateIcon fontSize="small" color="action" />
                          {adv.departure_date && adv.return_date
                            ? `${format(new Date(adv.departure_date), "dd/MM/yyyy")} – ${format(new Date(adv.return_date), "dd/MM/yyyy")}`
                            : "—"}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          icon={<MoneyIcon />}
                          label={Number(adv.total_cost || 0).toLocaleString()}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={actionLoadingId === adv.id ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
                          disabled={!!actionLoadingId}
                          onClick={() => handleApproveDisbursement(adv)}
                        >
                          {actionLoadingId === adv.id ? "Processing…" : "Approve disbursement"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TravelAdvancesNeedingPaymentPage;
